/**
 * Copyright © 2025 Farmlands Co-operative Society Limited. All rights reserved.
 *
 * This document contains confidential information and is the property of Farmlands.
 * Unauthorised reproduction or distribution of any part of this publication is prohibited without written consent.
 */

import { ReactNode, createElement } from 'react';
import { LookupValue } from '../../api/types/types';

/**
 * Checks if search characters appear in order in the label (allowing spaces between).
 * Returns true if all characters match in order, false otherwise.
 */
const hasSpacedMatch = (label: string, search: string): boolean => {
  const labelLower = label.toLowerCase();
  const searchLower = search.toLowerCase().replace(/\s+/g, '');
  let searchIndex = 0;

  for (let i = 0; i < labelLower.length && searchIndex < searchLower.length; i++) {
    if (labelLower[i] === searchLower[searchIndex]) {
      searchIndex++;
    }
  }

  return searchIndex === searchLower.length;
};

/**
 * Finds the indices of matched characters for spaced matches.
 * Returns an array of indices where each search character was found.
 */
const findSpacedMatchIndices = (label: string, search: string): number[] => {
  const labelLower = label.toLowerCase();
  const searchLower = search.toLowerCase().replace(/\s+/g, '');

  const result = Array.from(labelLower).reduce<{ indices: number[]; searchIndex: number }>(
    (acc, char, i) => {
      if (acc.searchIndex < searchLower.length && char === searchLower[acc.searchIndex]) {
        acc.indices.push(i);
        acc.searchIndex++;
      }
      return acc;
    },
    { indices: [], searchIndex: 0 },
  );

  return result.indices;
};

export const competitorFilterOptions = (options: LookupValue[], state: { inputValue: string }) => {
  const inputValue = state.inputValue.toLowerCase().trim();
  if (!inputValue) {
    return options;
  }

  const filtered = options.filter((option) => {
    const label = (option.label || '').toLowerCase().trim();
    // Check for continuous match first
    if (label.includes(inputValue)) {
      return true;
    }
    // Check for spaced match
    return hasSpacedMatch(label, inputValue);
  });

  // Sort by relevance: continuous matches first, then spaced matches
  return filtered.sort((a, b) => {
    const labelA = (a.label || '').toLowerCase().trim();
    const labelB = (b.label || '').toLowerCase().trim();

    const continuousMatchA = labelA.includes(inputValue);
    const continuousMatchB = labelB.includes(inputValue);

    // Continuous matches come before spaced matches
    if (continuousMatchA && !continuousMatchB) {
      return -1;
    }
    if (!continuousMatchA && continuousMatchB) {
      return 1;
    }

    // If both are continuous matches, sort by relevance
    if (continuousMatchA && continuousMatchB) {
      const startsWithA = labelA.startsWith(inputValue);
      const startsWithB = labelB.startsWith(inputValue);

      // Options that start with input come first
      if (startsWithA && !startsWithB) {
        return -1;
      }
      if (!startsWithA && startsWithB) {
        return 1;
      }

      // If both or neither start with input, sort by length (shorter = more exact match)
      if (startsWithA && startsWithB) {
        return labelA.length - labelB.length;
      }

      // For contains matches, shorter labels are more relevant
      return labelA.length - labelB.length;
    }

    // Both are spaced matches, sort by length
    return labelA.length - labelB.length;
  });
};

/**
 * Highlights matching characters in a label by wrapping them in bold tags.
 * Returns an array of text parts, where matching parts are wrapped in <strong> tags.
 * Supports both continuous matches and spaced matches.
 */
export const highlightMatchingText = (label: string, searchValue: string): ReactNode => {
  if (!searchValue || !label) {
    return label;
  }

  const searchLower = searchValue.toLowerCase();
  const labelLower = label.toLowerCase();

  // First try continuous match
  const continuousMatchIndex = labelLower.indexOf(searchLower);
  if (continuousMatchIndex !== -1) {
    const parts: ReactNode[] = [];
    let lastIndex = 0;
    let currentIndex = 0;
    let partIndex = 0;

    while (currentIndex < labelLower.length) {
      const matchIndex = labelLower.indexOf(searchLower, currentIndex);
      if (matchIndex === -1) {
        if (lastIndex < label.length) {
          const remainingText = label.substring(lastIndex);
          if (remainingText) {
            parts.push(remainingText);
          }
        }
        break;
      }

      if (matchIndex > lastIndex) {
        const textBefore = label.substring(lastIndex, matchIndex);
        parts.push(textBefore);
      }

      const matchedText = label.substring(matchIndex, matchIndex + searchValue.length);
      parts.push(createElement('strong', { key: `match-${partIndex++}` }, matchedText));

      lastIndex = matchIndex + searchValue.length;
      currentIndex = matchIndex + 1;
    }

    return parts.length > 0 ? createElement('span', {}, ...parts) : label;
  }

  // If no continuous match, try spaced match
  const spacedMatchIndices = findSpacedMatchIndices(label, searchValue);
  if (spacedMatchIndices.length > 0) {
    const parts: ReactNode[] = [];
    let lastIndex = 0;
    let partIndex = 0;

    spacedMatchIndices.forEach((matchIndex) => {
      // Add text before the match
      if (matchIndex > lastIndex) {
        const textBefore = label.substring(lastIndex, matchIndex);
        parts.push(textBefore);
      }

      // Add the matched character wrapped in bold
      const matchedChar = label.substring(matchIndex, matchIndex + 1);
      parts.push(createElement('strong', { key: `match-${partIndex++}` }, matchedChar));

      lastIndex = matchIndex + 1;
    });

    // Add remaining text after last match
    if (lastIndex < label.length) {
      const remainingText = label.substring(lastIndex);
      parts.push(remainingText);
    }

    return parts.length > 0 ? createElement('span', {}, ...parts) : label;
  }

  return label;
};

